/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The geofence editor's map: Mapbox with terra-draw for drawing and editing the shape. This is
 * the only module that touches terra-draw; the editor sees a controlled component that takes a
 * `GeofenceShape` and reports a new one after every finished edit.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import 'mapbox-gl/dist/mapbox-gl.css'
import MapboxMap, { Marker, type MapRef, NavigationControl } from 'react-map-gl/mapbox'
import {
  type GeoJSONStoreFeatures,
  type HexColor,
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawSelectMode,
  ValidateNotSelfIntersecting,
} from 'terra-draw'
import { TerraDrawMapboxGLAdapter } from 'terra-draw-mapbox-gl-adapter'

import { getZOrderColor } from '@/lib/map/GeofencesInView'
import { type MapStyle, MapStyleToggles, mapStyleUrl } from '@/lib/map/MapStyleToggles'

import { GeofenceLayers } from '../components/GeofenceLayers'
import { WORLD_VIEW, extentOf } from '../GeofenceOverviewMap'
import { boundsOf, closeRing, radiusMeters } from '../geometry'
import type { GeofenceShape, ManagedGeofence, ShapeType } from '../types'

const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN

const SELECT_MODE = 'select'
/** Circles are drawn as this many segments, on the globe, so they stay round away from the equator. */
const CIRCLE_SEGMENTS = 36
const COORDINATE_PRECISION = 7

const round = (value: number) => Number(value.toFixed(COORDINATE_PRECISION))

const SELECT_FLAGS = {
  polygon: {
    feature: {
      draggable: true,
      coordinates: { draggable: true, midpoints: true, deletable: true },
      validation: ValidateNotSelfIntersecting,
    },
  },
  rectangle: { feature: { draggable: true, coordinates: { resizable: 'opposite' as const } } },
  circle: { feature: { draggable: true, coordinates: { resizable: 'center' as const } } },
}

const shapeStyles = (color: HexColor) => ({
  fillColor: color,
  fillOpacity: 0.25,
  outlineColor: color,
  outlineWidth: 2,
})

const createDraw = (map: mapboxgl.Map, color: HexColor) =>
  new TerraDraw({
    adapter: new TerraDrawMapboxGLAdapter({ map, coordinatePrecision: COORDINATE_PRECISION }),
    modes: [
      new TerraDrawPolygonMode({
        styles: { ...shapeStyles(color), closingPointColor: color, coordinatePointColor: color },
        validation: (feature) => ValidateNotSelfIntersecting(feature),
      }),
      new TerraDrawRectangleMode({ styles: shapeStyles(color), drawInteraction: 'click-move-or-drag' }),
      new TerraDrawCircleMode({
        styles: shapeStyles(color),
        projection: 'globe',
        segments: CIRCLE_SEGMENTS,
        drawInteraction: 'click-move-or-drag',
      }),
      new TerraDrawSelectMode({
        flags: SELECT_FLAGS,
        // Clicking off the shape must not leave it unselected and uneditable.
        allowManualDeselection: false,
        // The editor's Clear button removes the shape; a stray Delete key must not.
        keyEvents: { deselect: null, delete: null, rotate: null, scale: null },
        styles: {
          selectedPolygonColor: color,
          selectedPolygonOutlineColor: color,
          selectedPolygonFillOpacity: 0.25,
          selectedPolygonOutlineWidth: 2,
          selectionPointColor: color,
          selectionPointOutlineColor: '#ffffff',
          midPointColor: '#ffffff',
          midPointOutlineColor: color,
        },
      }),
    ],
  })

const toFeature = (shape: GeofenceShape, mode: ShapeType): GeoJSONStoreFeatures => ({
  type: 'Feature',
  geometry: {
    type: 'Polygon',
    coordinates: [closeRing(shape.ring.map(([lng, lat]) => [round(lng), round(lat)] as [number, number]))],
  },
  properties: {
    mode,
    ...(mode === 'circle' ? { radiusKilometers: radiusMeters(shape.ring) / 1000 } : {}),
  },
})

const toShape = (feature: GeoJSONStoreFeatures | undefined, fallback: ShapeType): GeofenceShape | null => {
  if (feature?.geometry.type !== 'Polygon') return null
  const mode = feature.properties.mode
  const type: ShapeType = mode === 'circle' || mode === 'rectangle' || mode === 'polygon' ? mode : fallback
  return { type, ring: feature.geometry.coordinates[0] as [number, number][] }
}

export interface GeofenceDrawMapProps {
  /** The shape on the map, or null to draw a new one with `tool`. */
  shape: GeofenceShape | null
  tool: ShapeType
  /** Called after every finished draw or edit. */
  onChange: (shape: GeofenceShape) => void
  /** The geofence's layer: the shape is drawn in that layer's colour. */
  zorder: number
  /** The company's other geofences, drawn faintly so overlaps show. */
  context: ManagedGeofence[]
  /** A searched place to fly to and mark. */
  place?: { latitude: number; longitude: number }
  /** Bumped by the parent to throw away whatever is on the map (Clear), even a half-drawn shape. */
  resetToken?: number
}

/**
 * Draw or edit one geofence. With no shape, the map is in `tool`'s draw mode; once a shape is
 * finished (or one is loaded) it is selected for editing: drag it to move it, drag vertices or
 * midpoints of a polygon (right-click a vertex to remove it), drag a rectangle's corner or a
 * circle's edge to resize. A shape loaded from Fresh that terra-draw won't take as its own shape
 * (an odd Refresh rectangle) is edited as a polygon instead.
 */
export const GeofenceDrawMap = ({
  shape,
  tool,
  onChange,
  zorder,
  context,
  place,
  resetToken = 0,
}: GeofenceDrawMapProps) => {
  const mapRef = useRef<MapRef>(null)
  const drawRef = useRef<TerraDraw | null>(null)
  const [style, setStyle] = useState<MapStyle>('road')
  // Where a remounted map (after a style change) opens: where the old one was.
  const cameraRef = useRef<{ longitude: number; latitude: number; zoom: number }>(WORLD_VIEW)
  const contextRef = useRef(context)
  contextRef.current = context
  const [ready, setReady] = useState(false)
  // The last shape this component reported. A `shape` prop equal to it is the parent echoing the
  // edit back, so the drawing is left alone; any other shape (undo, clear, first load) is loaded.
  const reportedRef = useRef<GeofenceShape | null | undefined>(undefined)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const toolRef = useRef(tool)
  toolRef.current = tool
  const color = getZOrderColor(zorder) as HexColor

  const fitTo = useCallback((target: GeofenceShape | null) => {
    const bounds = target ? boundsOf(target.ring) : extentOf(contextRef.current)
    if (!bounds || !mapRef.current) return
    mapRef.current.fitBounds(
      [
        [bounds.west, bounds.south],
        [bounds.east, bounds.north],
      ],
      { padding: 80, maxZoom: 16, duration: 0 }
    )
  }, [])

  // terra-draw takes its layers off the map when stopped, so it must stop while the map is alive.
  const stopDraw = useCallback(() => {
    try {
      drawRef.current?.stop()
    } catch {
      // The map went first; its layers went with it.
    }
    drawRef.current = null
  }, [])

  const handleLoad = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) return
    const draw = createDraw(map, color)
    draw.on('finish', (id, { action }) => {
      const next = toShape(draw.getSnapshotFeature(id), toolRef.current)
      if (!next) return
      reportedRef.current = next
      onChangeRef.current(next)
      // A newly drawn shape goes straight into editing.
      if (action === 'draw') {
        draw.setMode(SELECT_MODE)
        draw.selectFeature(id)
      }
    })
    draw.on('ready', () => {
      drawRef.current = draw
      reportedRef.current = undefined
      setReady(true)
    })
    draw.start()
    // The draw is created with the colour of the moment; later changes go through setModeStyles.
  }, [])

  useEffect(() => stopDraw, [stopDraw])

  // Load the shape the parent holds when it isn't the one just reported, or when told to reset.
  const fittedRef = useRef(false)
  const resetRef = useRef(resetToken)
  useEffect(() => {
    const draw = drawRef.current
    const reset = resetRef.current !== resetToken
    resetRef.current = resetToken
    if (!ready || !draw || (shape === reportedRef.current && !reset)) return
    reportedRef.current = shape
    draw.clear()
    if (shape) {
      const [result] = draw.addFeatures([toFeature(shape, shape.type)])
      const id = result.valid ? result.id : draw.addFeatures([toFeature(shape, 'polygon')])[0].id
      draw.setMode(SELECT_MODE)
      if (id !== undefined) draw.selectFeature(id)
    } else {
      draw.setMode(toolRef.current)
    }
    if (!fittedRef.current) {
      fittedRef.current = true
      fitTo(shape)
    }
  }, [ready, shape, resetToken, fitTo])

  // A new tool while nothing is drawn: draw with it.
  useEffect(() => {
    const draw = drawRef.current
    if (!ready || !draw || shape) return
    draw.setMode(tool)
  }, [ready, tool, shape])

  useEffect(() => {
    const draw = drawRef.current
    if (!ready || !draw) return
    const styles = shapeStyles(color)
    draw.setModeStyles('polygon', { ...styles, closingPointColor: color, coordinatePointColor: color })
    draw.setModeStyles('rectangle', styles)
    draw.setModeStyles('circle', styles)
    draw.setModeStyles(SELECT_MODE, {
      selectedPolygonColor: color,
      selectedPolygonOutlineColor: color,
      selectedPolygonFillOpacity: 0.25,
      selectedPolygonOutlineWidth: 2,
      selectionPointColor: color,
      selectionPointOutlineColor: '#ffffff',
      midPointColor: '#ffffff',
      midPointOutlineColor: color,
    })
  }, [ready, color])

  useEffect(() => {
    if (place) mapRef.current?.flyTo({ center: [place.longitude, place.latitude], zoom: 16, duration: 800 })
  }, [place])

  // No shape yet and no other geofences: the company has nothing to fit to, so a fit waits for
  // the context to arrive.
  useEffect(() => {
    if (ready && !fittedRef.current && context.length > 0 && !shape) {
      fittedRef.current = true
      fitTo(null)
    }
  }, [ready, context.length, shape, fitTo])

  return (
    <div className="relative h-full w-full" data-testid="geofence-draw-map">
      <MapboxMap
        key={style}
        ref={mapRef}
        mapboxAccessToken={mapboxToken}
        mapStyle={mapStyleUrl(style)}
        initialViewState={cameraRef.current}
        style={{ width: '100%', height: '100%' }}
        doubleClickZoom={false}
        onLoad={handleLoad}
      >
        <NavigationControl position="bottom-right" showCompass={false} />
        <GeofenceLayers geofences={context} muted idPrefix="geofence-context" />
        {place && <Marker longitude={place.longitude} latitude={place.latitude} color="#ef4444" />}
      </MapboxMap>
      <MapStyleToggles
        style={style}
        onChange={(next) => {
          // The new map gets a new terra-draw, which reloads the shape where the view was.
          const map = mapRef.current
          if (map) {
            const center = map.getCenter()
            cameraRef.current = { longitude: center.lng, latitude: center.lat, zoom: map.getZoom() }
          }
          stopDraw()
          setReady(false)
          reportedRef.current = undefined
          fittedRef.current = true
          setStyle(next)
        }}
        show3d={false}
      />
    </div>
  )
}
